import { inject, singleton } from 'tsyringe';
import {
  OWWindow,
  OWWindowScreenAnchor,
  OWWindowsListenerDelegate,
  StopWatch,
} from '../../odk-ts';
import {
  kNameOfPromotionWindowService,
  IPromotionWindowService,
} from '../../shared/interfaces/promotion-window-service.interface';
import { LoggerService } from '../../shared/services/logger.service';
import { FunctionPropertyNames } from '../../shared/types';
import {
  kDITokenPromotionWindowScreenAnchor,
  kDITokenOwPromotionWindow,
  kPromotionWindowName,
} from '../constants';
import {
  IPromotionWindowManagerService,
  IPromotionWindowManagerServiceDelegate,
} from '../interfaces/promotion-window-manager-service.interface';

import { OWWindowListenerWrapper } from '../../shared/wrappers/ow-window-listener-wrapper';
import { PromotionDetailsBuilderService } from './promotion-details-builder.service';
import { UserActionService } from './user-action.service';
import { PromotionAnalyticsService } from './promotion-analytics/promotion-analytics.service';
import { ContentRecommendationUserAction } from '../../shared/constants';
import { PromotionDisplayDetails } from '../../shared/models/promotion-display-details.model';
import { RecommendationDatastoreService } from './datastore-service/recommendation-datastore.service';
import {
  ITimerServiceDelegate,
  TimerService,
} from '../../shared/services/timer.service';
import { MonitorHelper } from '@overwolf/api-ts/window/utils/monitor_helper';
import { Edge } from '@overwolf/api-ts/window/enums/edge';
import { OSRWindow } from '@overwolf/api-ts';
import { OSRType } from '@overwolf/api-ts/window/enums/osr_window_type';
import { PromotionType } from './datastore-service/models/raw-promotion.model';
import { OWSettingsGamesWrapper } from '../ow-odk-wrappers/ow-settings-games-wrapper';

const kTimeForTesterPromotionInMs = 60000;

const kGameWindowPositioningMap = new Map<
  number,
  { edge: Edge; marginX: number; marginY: number }
>([
  [21216, { edge: Edge.TopRight, marginX: 75, marginY: 393 }],
  [4688, { edge: Edge.TopRight, marginX: 30, marginY: 60 }],
  [22174, { edge: Edge.TopRight, marginX: 30, marginY: 60 }],
  [0, { edge: Edge.TopRight, marginX: 20, marginY: 20 }],
]);

// -----------------------------------------------------------------------------
@singleton()
// ITimerServiceDelegate,
export class PromotionWindowService
  implements
    IPromotionWindowService,
    IPromotionWindowManagerService,
    ITimerServiceDelegate,
    OWWindowsListenerDelegate
{
  private readonly logger = LoggerService.getCategory('PromotionWindowService');

  private readonly listeners: Set<IPromotionWindowManagerServiceDelegate>;
  private activePromotionInfo: PromotionDisplayDetails;
  private isTesterPromotion: boolean;
  private promotionWindow: OSRWindow;

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kDITokenOwPromotionWindow)
    private readonly owPromotionWindow: OWWindow,
    @inject(kDITokenPromotionWindowScreenAnchor)
    private readonly owWindowScreenAnchor: OWWindowScreenAnchor,
    private readonly owSettingsGamesWrapper: OWSettingsGamesWrapper,
    private readonly datastoreService: RecommendationDatastoreService,
    private readonly owWindowListener: OWWindowListenerWrapper,
    private readonly promotionVisibilityStopWatch: StopWatch,
    private readonly timerService: TimerService,
    private readonly userActionService: UserActionService,
    private readonly promotionDetailsBuilder: PromotionDetailsBuilderService,
    private readonly promotionAnalyticsService: PromotionAnalyticsService,
  ) {
    this.listeners = new Set<IPromotionWindowManagerServiceDelegate>();
    this.isTesterPromotion = false;
    this.timerService.addListener(this);
  }

  // ---------------------------------------------------------------------------
  // IPromotionWindowManagerService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  async show(
    gameId: number,
    promotionId: string,
    promotionType: PromotionType,
    isTester = false,
    useDevEnv: string,
  ) {
    this.logger.info(`Showing promotion for game: ${gameId}`);
    this.registerListeners();
    this.activePromotionInfo =
      await this.promotionDetailsBuilder.getPromotionDetails(
        gameId,
        promotionId,
        promotionType,
        useDevEnv,
      );

    if (promotionType === PromotionType.Offer) {
      await this.createPromotionOfferWindow(isTester);
    } else {
      await this.createPromotionWindow(isTester);
    }
  }

  // ---------------------------------------------------------------------------
  async createPromotionWindow(isTester = false) {
    // const widthMarginRatio = 1920 / window.screen.width;
    // const heightMarginRatio = 1080 / window.screen.height;

    const ratio = window.devicePixelRatio;

    const baseWidth = 384;
    const baseHeight = 294;

    this.promotionWindow = new OSRWindow({
      id: 'promotion',
      url: 'dist/promotion/promotion.html',
      visible: false,
      width: baseWidth,
      height: baseHeight,
      minWidth: baseWidth,
      minHeight: baseHeight,
      autoDpi: false,
      autoZoom: false,
      transparent: true,
      resizable: false,
      showInTaskBar: false,
      isAltF4Blocked: true,
      topMost: true,
      type: OSRType.InGameOnly,
    });

    this.promotionWindow.on('closed', () => {
      this.onWindowClosed(kPromotionWindowName);
    });

    await this.promotionWindow.assureCreated();

    let width, height;
    if (!isTester) {
      const gameWindow = await MonitorHelper.getWindowMonitor(
        this.promotionWindow,
      );

      width = ratio === 1 ? gameWindow.width : gameWindow.width / ratio;
      height = ratio === 1 ? gameWindow.height : gameWindow.height / ratio;
    } else {
      width = ratio === 1 ? window.screen.width : window.screen.width / ratio;
      height =
        ratio === 1 ? window.screen.height : window.screen.height / ratio;
    }

    if (width !== baseWidth && height !== baseHeight) {
      this.promotionWindow.setSize({ width, height });
    }

    const positioning =
      kGameWindowPositioningMap.get(this.activePromotionInfo.gameId) ??
      kGameWindowPositioningMap.get(0);

    await this.promotionWindow.anchor(positioning.edge, {
      marginX: positioning.marginX,
      marginY: positioning.marginY,
    });

    await this.promotionWindow.show();
    this.startIdleTimer();
    if (isTester) {
      this.isTesterPromotion = true;
    }
  }

  // ---------------------------------------------------------------------------
  async createPromotionOfferWindow(isTester = false) {
    await this.waitForOverlayIfNecessary(isTester);

    const baseWidth = 436;
    const baseHeight = 343;

    this.promotionWindow = new OSRWindow({
      id: 'promotion',
      url: 'dist/promotion/promotion.html',
      visible: false,
      width: baseWidth,
      height: baseHeight,
      minWidth: baseWidth,
      minHeight: baseHeight,
      autoDpi: false,
      autoZoom: false,
      transparent: true,
      resizable: false,
      showInTaskBar: false,
      isAltF4Blocked: true,
      topMost: true,
      type: OSRType.Default,
    });

    this.promotionWindow.on('closed', () => {
      this.onWindowClosed(kPromotionWindowName);
    });

    await this.promotionWindow.assureCreated();

    //NOTE(gsarusi): This seems not to be working as expected, commenting out for now
    // let width, height;
    // if (!isTester) {
    //   const gameWindow = await MonitorHelper.getWindowMonitor(
    //     this.promotionWindow,
    //   );

    //   width = ratio === 1 ? baseWidth : baseWidth / ratio;
    //   height = ratio === 1 ? baseHeight : baseHeight / ratio;
    // } else {
    //   width = ratio === 1 ? window.screen.width : window.screen.width / ratio;
    //   height =
    //     ratio === 1 ? window.screen.height : window.screen.height / ratio;
    // }

    // if (width !== baseWidth && height !== baseHeight) {
    //   this.promotionWindow.setSize({ width, height });
    // }

    await this.promotionWindow.anchor(Edge.BottomRight, {
      marginX: 50,
      marginY: 50,
    });

    await this.promotionWindow.setTopmost(true);

    await this.promotionWindow.show();
    this.startIdleTimer();
    if (isTester) {
      this.isTesterPromotion = true;
    }
  }

  // ---------------------------------------------------------------------------
  close() {
    this.owPromotionWindow.close();
  }

  // ---------------------------------------------------------------------------
  async isPromotionShown(): Promise<boolean> {
    const state = await this.owPromotionWindow.getWindowState();
    if (state.window_state_ex === overwolf.windows.enums.WindowStateEx.closed) {
      return false;
    }

    return true;
  }

  // ---------------------------------------------------------------------------
  getPromotionInfo(): PromotionDisplayDetails {
    return this.activePromotionInfo;
  }

  // ---------------------------------------------------------------------------
  private async registerListeners() {
    this.unregisterListeners();
    this.owWindowListener.addListener(this);
  }

  // ---------------------------------------------------------------------------
  private async unregisterListeners() {
    this.owWindowListener.removeListener(this);
  }

  // ---------------------------------------------------------------------------
  private async startIdleTimer() {
    const configs = await this.datastoreService.getGlobalConfigurations();
    const intervalInMs = this.isTesterPromotion
      ? kTimeForTesterPromotionInMs
      : configs.autoDismissPromotionsInSeconds * 1000;
    this.timerService.initialize({ intervalInMs });
    this.timerService.start();
  }

  // ---------------------------------------------------------------------------
  private stopIdleTimer() {
    this.timerService.stop();
  }

  // ---------------------------------------------------------------------------
  private triggerListenerEvent(
    eventName: FunctionPropertyNames<IPromotionWindowManagerServiceDelegate>,
    ...params: any[]
  ) {
    if (!eventName) {
      return;
    }

    for (const listener of this.listeners) {
      if (!listener[eventName]) {
        continue;
      }

      // eslint-disable-next-line prefer-spread
      listener[eventName].apply(listener, params);
    }
  }

  // ---------------------------------------------------------------------------
  private async handlePromotionViewed() {
    const config = await this.datastoreService.getGlobalConfigurations();
    setTimeout(() => {
      this.triggerListenerEvent(
        'onPromotionReachedMinTimeToDisplay',
        this.activePromotionInfo,
      );
    }, config.minimumVisibleTimeToCountInSeconds * 1000);
  }

  // ---------------------------------------------------------------------------
  private async waitForOverlayIfNecessary(isTester: boolean) {
    if (isTester) {
      return;
    }

    const overlayEnabled = await this.owSettingsGamesWrapper.getOverlayEnabled(
      this.activePromotionInfo.gameId,
    );

    if (overlayEnabled) {
      this.logger.info(
        'Overlay is enabled for the game, waiting for 5 seconds before showing promotion',
      );
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }
  }

  // ---------------------------------------------------------------------------
  // ExposableService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  get token(): string {
    return kNameOfPromotionWindowService;
  }

  // ---------------------------------------------------------------------------
  // PromotionWindowManagerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  addListener(listener: IPromotionWindowManagerServiceDelegate) {
    this.listeners.add(listener);
  }

  // ---------------------------------------------------------------------------
  removeListener(listener: IPromotionWindowManagerServiceDelegate) {
    this.listeners.delete(listener);
  }

  // ---------------------------------------------------------------------------
  // IPromotionWindowService
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // When the promotion become visible, we reset the timer so that the time
  // is counted when the promotion is in view
  async promotionBecameVisible() {
    this.logger.info('Recommendation became visible, restarting timer');
    this.promotionVisibilityStopWatch.start();

    await this.handlePromotionViewed();
  }

  // ---------------------------------------------------------------------------
  getPromotionStopWatchTime() {
    return this.promotionVisibilityStopWatch.getTime();
  }

  // ---------------------------------------------------------------------------
  getActivePromotion(): PromotionDisplayDetails {
    return this.activePromotionInfo;
  }

  // ---------------------------------------------------------------------------
  closePromotionWindow() {
    this.close();
  }

  // ---------------------------------------------------------------------------
  toggleTimeoutTimer(pause: boolean) {
    if (pause) {
      this.stopIdleTimer();
      return;
    }

    this.timerService.start();
  }

  // ---------------------------------------------------------------------------
  // OWWindowsListenerDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onAltF4Blocked(windowName: string) {
    this.logger.info(
      `received Alt+F4 key combination on windowId: ${windowName} `,
    );

    if (!windowName.endsWith(kPromotionWindowName)) {
      return;
    }

    this.promotionVisibilityStopWatch.stop();
    this.userActionService.promotionDismissed(this.activePromotionInfo);
    this.close();
  }

  // ---------------------------------------------------------------------------
  onWindowClosed(windowName: string) {
    if (!windowName.endsWith(kPromotionWindowName)) {
      return;
    }

    this.stopIdleTimer();
    this.unregisterListeners();
    this.logger.info(`Promotion window, windowId: "${windowName}" closed,`);
    const visibilityTimeInSeconds =
      this.promotionVisibilityStopWatch.getTime() / 1000;
    this.promotionVisibilityStopWatch.stop();

    if (!this.isTesterPromotion) {
      this.triggerListenerEvent(
        'onPromotionWindowClosed',
        this.activePromotionInfo,
        visibilityTimeInSeconds,
      );
    }
  }

  // ---------------------------------------------------------------------------
  // TimerServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onTimer() {
    this.logger.info('Promotion timer has reached an end, closing promotion ');
    this.promotionVisibilityStopWatch.stop();

    this.promotionAnalyticsService.reportContentRecommend(
      this.activePromotionInfo.gameId,
      this.activePromotionInfo.promotionId,
      this.activePromotionInfo.promotionType,
      ContentRecommendationUserAction.Timeout,
    );

    this.close();
  }
}
